<script lang="ts" setup>
import type { ProductPriceInfo } from "~~/models/products";
import { PlanktonPredefinedRight } from "~~/server/plankton";

const { price, isPricesLoading } = defineProps<{
  viewMode: "list" | "details";
  price?: ProductPriceInfo | null | undefined;
  isPricesLoading?: boolean;
  isVariableWeight?: boolean;
  showDiscountText?: boolean;
}>();

const { t } = useI18n();
const { user } = useUserSession();

const showPriceAvailable = hasAllRights(user.value, PlanktonPredefinedRight.ShowPriceNetAvailable, PlanktonPredefinedRight.ShowPriceGrossAvailable);

const isSafePricesLoading = computed(() => isPricesLoading && showPriceAvailable);

const discountText = computed(() => price?.discountAmount
  ? `- ${formatCurrency(price?.discountAmount)}`
  : `${price?.discountPercentage}%`);
</script>

<template>
  <div>
    <div v-if="isSafePricesLoading">
      <USkeleton class="w-2/5" :class="{ 'h-[27px]': viewMode === 'list', 'h-[48px]': viewMode === 'details' }" />
    </div>

    <div v-else>
      <div v-if="showPriceAvailable && price && (price.salesPrice > 0 || price.originalPrice > 0)">
        <div v-if="viewMode === 'list'" class="gap-xs flex flex-wrap items-center">
          <span v-if="price.isPromo" class="text-primary text-nowrap font-bold">{{ formatCurrency(price.salesPrice) }}</span>
          <span v-else class="text-nowrap font-bold">{{ formatCurrency(price.salesPrice) }}</span>

          <span v-if="isVariableWeight" class="font-bold">{{ t("product.price.per-kg") }}</span>

          <div v-if="price.isPromo" class="gap-xs flex">
            <span class="text-nowrap text-xs font-medium line-through">{{ formatCurrency(price.originalPrice) }}</span>
            <span v-if="!isVariableWeight && showDiscountText" class="text-primary text-nowrap text-xs font-medium">{{ discountText }}</span>
          </div>
        </div>

        <div v-if="viewMode === 'details'">
          <div v-if="price.isPromo && showDiscountText" class="text-primary text-nowrap text-sm font-medium uppercase">
            {{ t("product.price.discount") }} {{ discountText }}
          </div>

          <div class="inline-block align-text-bottom">
            <div class="inline-block">
              <span v-if="price.isPromo" class="text-primary text-nowrap text-[32px] font-bold">{{ formatCurrency(price.salesPrice) }}</span>
              <span v-else class="text-nowrap text-[32px] font-bold">{{ formatCurrency(price.salesPrice) }}</span>

              <span v-if="isVariableWeight" class="ms-xs text-[32px] font-bold">{{ t("product.price.per-kg") }}</span>
            </div>

            <div class="ms-md gap-xs inline-flex">
              <span v-if="price.isPromo" class="text-sm font-medium text-neutral-400 line-through">{{ formatCurrency(price.originalPrice) }}</span>
              <span class="text-sm font-medium text-neutral-400">{{ t("product.price.excl-vat") }}</span>
            </div>
          </div>
        </div>
        <div v-if="price?.variableWeightPrice" class="text-xs text-neutral-500">
          <span>{{ formatCurrency(price?.variableWeightPrice) }} {{ t("product.price.per-kg") }}</span>
        </div>
      </div>

      <div v-else>
        <span class="font-bold" :class="{ 'text-[32px]': viewMode === 'details', 'text-transparent': !showPriceAvailable }">
          {{ t("product.price.unavailable") }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
